// Admin › Settings › Referral Program › Referrals and Ledger tabs (rewards part 4).
import type { ReferralAction, ReferralStage } from '../../api/referralRewards'
import type { LocalizedDict } from '../index'

export type RewardsReferralsStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  empty: string
  columns: { seller: string; referrer: string; stage: string; progress: string; rewards: string; actions: string }
  stages: Record<ReferralStage, string>
  feePaid: string
  feeMissing: string
  feeRefunded: string
  days: (n: number, min: number) => string
  orders: (n: number, min: number) => string
  actions: Record<ReferralAction, string>
  dialogTitle: Record<ReferralAction, (seller: string) => string>
  dialogBody: Record<ReferralAction, string>
  reasonLabel: string
  reasonPlaceholder: string
  reasonRequired: string
  cancel: string
  saving: string
}

export type RewardsLedgerStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  empty: string
  columns: { date: string; seller: string; movement: string; amount: string; expires: string; left: string }
  kinds: { granted: string; taken_back: string }
  sources: Record<string, string>
  never: string
  // Part 6: manual credit.
  grantButton: string
  takeBack: string
  grantTitle: string
  grantBody: string
  pickSeller: string
  changeSeller: string
  pickerAction: string
  pickerDescription: string
  sellerRequired: string
  amountLabel: string
  amountInvalid: string
  expiresLabel: string
  expiresHint: string
  takeBackTitle: (seller: string, amount: string) => string
  takeBackBody: string
  reasonLabel: string
  reasonPlaceholder: string
  reasonRequired: string
  cancel: string
  saving: string
  grantSubmit: string
}

export type RewardsCostStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  columns: {
    month: string
    fees: string
    granted: string
    takenBack: string
    cash: string
    converted: string
    net: string
  }
  netHint: string
  convertedHint: string
  topTitle: string
  topEmpty: string
  topColumns: { referrer: string; earned: string }
  unknownReferrer: string
}

export const rewardsReferralsI18n: LocalizedDict<RewardsReferralsStrings> = {
  en: {
    title: 'Rewards referrals',
    subtitle: 'Sellers brought in under the rewards program. A referral counts once the fee is paid, the days have passed and enough orders shipped; it is checked every night.',
    loading: 'Loading…',
    loadError: "Couldn't load the referrals.",
    retry: 'Try again',
    empty: 'No one has joined with a rewards code yet.',
    columns: { seller: 'Seller', referrer: 'Brought in by', stage: 'Status', progress: 'Progress', rewards: 'Rewards', actions: 'Actions' },
    stages: { signed_up: 'Signed up', activated: 'Joined', qualified: 'Counts', rejected: 'Rejected', reversed: 'Taken back' },
    feePaid: 'Fee paid',
    feeMissing: 'Fee not paid',
    feeRefunded: 'Fee refunded',
    days: (n, min) => `${Math.min(n, min)}/${min} days`,
    orders: (n, min) => `${Math.min(n, min)}/${min} orders`,
    actions: { qualify: 'Count it', reject: 'Reject', reverse: 'Take back' },
    dialogTitle: {
      qualify: (seller) => `Count ${seller} now?`,
      reject: (seller) => `Reject ${seller}?`,
      reverse: (seller) => `Take back ${seller}?`,
    },
    dialogBody: {
      qualify: 'It counts without waiting for the rules, and the reward it earns is paid right away.',
      reject: 'It will never count. This cannot be undone.',
      reverse: 'It stops counting. Rewards it no longer earns are removed — only the unspent part.',
    },
    reasonLabel: 'Reason',
    reasonPlaceholder: 'e.g. Same owner as an existing seller',
    reasonRequired: 'Write at least 3 characters.',
    cancel: 'Cancel',
    saving: 'Saving…',
  },
  tr: {
    title: 'Ödül programı referansları',
    subtitle: 'Ödül programıyla getirilen satıcılar. Ücret ödenip gün dolunca ve yeterli sipariş gönderilince referans sayılır; kontrol her gece yapılır.',
    loading: 'Yükleniyor…',
    loadError: 'Referanslar yüklenemedi.',
    retry: 'Tekrar dene',
    empty: 'Henüz ödül koduyla katılan yok.',
    columns: { seller: 'Satıcı', referrer: 'Getiren', stage: 'Durum', progress: 'İlerleme', rewards: 'Ödül', actions: 'İşlemler' },
    stages: { signed_up: 'Kaydoldu', activated: 'Katıldı', qualified: 'Sayıldı', rejected: 'Reddedildi', reversed: 'Geri alındı' },
    feePaid: 'Ücret ödendi',
    feeMissing: 'Ücret ödenmedi',
    feeRefunded: 'Ücret iade edildi',
    days: (n, min) => `${Math.min(n, min)}/${min} gün`,
    orders: (n, min) => `${Math.min(n, min)}/${min} sipariş`,
    actions: { qualify: 'Say', reject: 'Reddet', reverse: 'Geri al' },
    dialogTitle: {
      qualify: (seller) => `${seller} şimdi sayılsın mı?`,
      reject: (seller) => `${seller} reddedilsin mi?`,
      reverse: (seller) => `${seller} geri alınsın mı?`,
    },
    dialogBody: {
      qualify: 'Kuralları beklemeden sayılır; hak ettiği ödül hemen verilir.',
      reject: 'Hiçbir zaman sayılmaz. Geri alınamaz.',
      reverse: 'Artık sayılmaz. Hak edilmeyen ödüller geri alınır — yalnız harcanmamış kısım.',
    },
    reasonLabel: 'Gerekçe',
    reasonPlaceholder: 'ör. Mevcut bir satıcıyla aynı sahip',
    reasonRequired: 'En az 3 karakter yaz.',
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
  },
}

export const rewardsLedgerI18n: LocalizedDict<RewardsLedgerStrings> = {
  en: {
    title: 'Reward credit ledger',
    subtitle: 'The newest credit granted and taken back across all wallets.',
    loading: 'Loading…',
    loadError: "Couldn't load the ledger.",
    retry: 'Try again',
    empty: 'No reward credit has moved yet.',
    columns: { date: 'Date', seller: 'Seller', movement: 'Movement', amount: 'Amount', expires: 'Expires', left: 'Left' },
    kinds: { granted: 'Granted', taken_back: 'Taken back' },
    sources: {
      entry: 'Entry credit',
      threshold: 'Big reward',
      per_referral: 'Referral reward',
      commission: 'Commission',
      conversion_base: 'Converted',
      conversion_bonus: 'Conversion bonus',
      manual: 'Manual',
    },
    never: 'Never',
    grantButton: 'Give credit',
    takeBack: 'Take back',
    grantTitle: 'Give reward credit',
    grantBody: 'The credit lands in the seller’s wallet now and they get a message. It can be spent on orders, never withdrawn.',
    pickSeller: 'Choose seller',
    changeSeller: 'Change',
    pickerAction: 'Choose',
    pickerDescription: 'Pick the seller whose wallet gets the credit.',
    sellerRequired: 'Choose a seller.',
    amountLabel: 'Amount (USD)',
    amountInvalid: 'Enter an amount between $0.01 and $10,000.',
    expiresLabel: 'Last day to spend',
    expiresHint: 'Leave empty and it never expires.',
    takeBackTitle: (seller, amount) => `Take back ${amount} from ${seller}?`,
    takeBackBody: 'Only the unspent part goes; what was already spent stays spent.',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'e.g. Shipping mix-up on order 1042',
    reasonRequired: 'Write at least 3 characters.',
    cancel: 'Cancel',
    saving: 'Saving…',
    grantSubmit: 'Give credit',
  },
  tr: {
    title: 'Ödül kredisi hareketleri',
    subtitle: 'Tüm cüzdanlarda en son verilen ve geri alınan krediler.',
    loading: 'Yükleniyor…',
    loadError: 'Hareketler yüklenemedi.',
    retry: 'Tekrar dene',
    empty: 'Henüz ödül kredisi hareketi yok.',
    columns: { date: 'Tarih', seller: 'Satıcı', movement: 'Hareket', amount: 'Tutar', expires: 'Süresi', left: 'Kalan' },
    kinds: { granted: 'Verildi', taken_back: 'Geri alındı' },
    sources: {
      entry: 'Giriş kredisi',
      threshold: 'Büyük ödül',
      per_referral: 'Referans ödülü',
      commission: 'Pay',
      conversion_base: 'Çevrilen',
      conversion_bonus: 'Çevirme bonusu',
      manual: 'Elle',
    },
    never: 'Süresiz',
    grantButton: 'Kredi ver',
    takeBack: 'Geri al',
    grantTitle: 'Ödül kredisi ver',
    grantBody: 'Kredi hemen satıcının cüzdanına yatar ve satıcıya mesaj gider. Siparişlerde harcanır, nakit çekilemez.',
    pickSeller: 'Satıcı seç',
    changeSeller: 'Değiştir',
    pickerAction: 'Seç',
    pickerDescription: 'Krediyi alacak satıcıyı seç.',
    sellerRequired: 'Bir satıcı seç.',
    amountLabel: 'Tutar (USD)',
    amountInvalid: '0,01 $ ile 10.000 $ arasında bir tutar yaz.',
    expiresLabel: 'Son harcama günü',
    expiresHint: 'Boş bırakırsan süresi dolmaz.',
    takeBackTitle: (seller, amount) => `${seller} satıcısından ${amount} geri alınsın mı?`,
    takeBackBody: 'Yalnız harcanmamış kısım alınır; harcanan harcanmış kalır.',
    reasonLabel: 'Gerekçe',
    reasonPlaceholder: 'ör. 1042 numaralı siparişte kargo karışıklığı',
    reasonRequired: 'En az 3 karakter yaz.',
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
    grantSubmit: 'Kredi ver',
  },
}

export const rewardsCostI18n: LocalizedDict<RewardsCostStrings> = {
  en: {
    title: 'Program cost',
    subtitle: 'What the rewards program cost each month (New York time), against the entry fees it brought in.',
    loading: 'Loading…',
    loadError: "Couldn't load the cost report.",
    retry: 'Try again',
    columns: {
      month: 'Month',
      fees: 'Entry fees',
      granted: 'Credit given',
      takenBack: 'Credit taken back',
      cash: 'Cash commission',
      converted: 'Converted',
      net: 'Net cost',
    },
    netHint: 'Net cost = credit given − credit taken back + cash commission − entry fees. Below zero means the fees paid for the program.',
    convertedHint: 'Converted is cash commission an affiliate turned into credit; only its bonus is counted as credit given.',
    topTitle: 'Top referrers (last 12 months)',
    topEmpty: 'No one has earned rewards yet.',
    topColumns: { referrer: 'Referrer', earned: 'Earned' },
    unknownReferrer: 'Unknown',
  },
  tr: {
    title: 'Program maliyeti',
    subtitle: 'Ödül programının her ay (New York saati) neye mal olduğu ve getirdiği giriş ücretleri.',
    loading: 'Yükleniyor…',
    loadError: 'Maliyet raporu yüklenemedi.',
    retry: 'Tekrar dene',
    columns: {
      month: 'Ay',
      fees: 'Giriş ücretleri',
      granted: 'Verilen kredi',
      takenBack: 'Geri alınan kredi',
      cash: 'Nakit pay',
      converted: 'Krediye çevrilen',
      net: 'Net maliyet',
    },
    netHint: 'Net maliyet = verilen kredi − geri alınan kredi + nakit pay − giriş ücretleri. Sıfırın altıysa ücretler programı karşılamış demektir.',
    convertedHint: 'Krediye çevrilen, affiliate’in krediye çevirdiği nakit paydır; yalnız bonusu verilen krediye sayılır.',
    topTitle: 'En çok kazandıranlar (son 12 ay)',
    topEmpty: 'Henüz kimse ödül kazanmadı.',
    topColumns: { referrer: 'Getiren', earned: 'Kazandığı' },
    unknownReferrer: 'Bilinmiyor',
  },
}
